import React from "react";
import { FormikProps, FieldArray } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { SocialMediaFormValues } from "src/models/SocialMedia.model";
import FileUploaderWrapper from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import { MdDeleteForever } from "react-icons/md";

type Props = {
  formikProps: FormikProps<SocialMediaFormValues>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const SocialMediaForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add Social Media" : "Update Social Media"}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>

      {/* Form Fields */}
      <FieldArray name="content">
        {({ push, remove }) => (
          <div className="flex flex-col gap-4">
            {values.content && values.content.length > 0 ? (
              values.content.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-4 border p-4 rounded-lg"
                >
                  {/* Name + Remove Button in Row */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <ATMTextField
                        name={`content[${index}].name`}
                        required
                        value={item.name}
                        onChange={(e) =>
                          setFieldValue(
                            `content[${index}].name`,
                            e.target.value
                          )
                        }
                        label="Name"
                        onBlur={handleBlur}
                      />
                    </div>

                    {values.content.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="p-2 mt-6"
                      >
                        <MdDeleteForever color="red" size={24} />
                      </button>
                    )}
                  </div>

                  {/* Icon */}
                  <FileUploaderWrapper
                    size="small"
                    label="Icon"
                    required
                    placeholder="Upload Icon"
                    selectedFile={item?.icon || ""}
                    disabled={false}
                    onSelect={(file) => {
                      setFieldValue(`content[${index}].icon`, file);
                    }}
                    name={`content[${index}].icon`}
                    accept="image/*"
                  />

                  {/* Link */}
                  <ATMTextField
                    name={`content[${index}].link`}
                    required
                    value={item.link}
                    onChange={(e) =>
                      setFieldValue(`content[${index}].link`, e.target.value)
                    }
                    label="Link"
                    onBlur={handleBlur}
                  />
                </div>
              ))
            ) : (
              <p>No social media links added</p>
            )}

            {/* Add Button */}
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={() =>
                  push({
                    name: "",
                    icon: null,
                    link: "",
                  })
                }
                className="rounded border-dashed border border-slate-500 font-medium text-md text-gray-500 flex items-center gap-1 px-3 py-1 hover:bg-gray-50"
              >
                Add {values.content?.length ? "More" : "Social Media"}
              </button>
            </div>
          </div>
        )}
      </FieldArray>

      {/* Submit */}
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default SocialMediaForm;
